<template>
    <div class="sticky top-0 z-50">
        <div
            :data-theme="settings.mainNav_ColorTheme"
            :data-mode="settings.mainNav_ColorPalette"
            class="top-0 inset-x-0 flex z-10 px-11 py-6 transition-all duration-300"
            :class="{
                'm-3 radius-card-lg': bannerHasBorder && navOverlayBanner && !hasScrolled,
                'absolute': navOverlayBanner,
                'relative': !navOverlayBanner,
                'h-[92px] lg:h-[128px] items-start bg-gradient-to-t from-transparent border-transparent to-black/40': navOverlayBanner && !hasScrolled,
                'h-[92px] lg:h-[92px] items-start surface-background border-b border-subtle': !navOverlayBanner || hasScrolled,
            }"
        >
            <!-- logo -->
            <div
                class="flex-1 flex items-center cursor-pointer h-full [&_img]:h-full"
                :class="{

                    'order-1': logoPosition === 'left',
                    'order-3 justify-center': logoPosition === 'centred',
                }"
            >
                <NuxtLink
                    :class="{
                        'h-full': logoPosition === 'left',
                        'absolute left-1/2 -translate-x-1/2 inset-y-6': logoPosition === 'centred',
                    }"
                    :to="homeUri"
                    no-prefetch
                    aria-label="Home"
                >
                    <slot name="logo" :sticky="hasScrolled" />
                </NuxtLink>
            </div>

            <!-- search -->
            <div
                v-if="showSearch"
                class="hidden md:flex items-center space-x-2"
                :class="{
                    'order-2 ml-auto mr-2': logoPosition === 'left',
                    'order-4': logoPosition === 'centred',
                }"
            >
                <NuxtLink to="/search" no-prefetch aria-label="Search">
                    <DButton
                        theme="transparent"
                        icon="magnifying-glass"
                        icon-set="ph"
                        size="lg"
                    />
                </NuxtLink>
                <!-- line -->
                <span v-if="logoPosition === 'left'" class="block w-px h-[22px] bg-[--border-base]" />
            </div>

            <!-- quicklinks -->
            <div
                v-if="quicklinks?.mainNavQuicklinksItem_Links && quicklinks.mainNavQuicklinksItem_Links.length > 0"
                class="hidden sm:inline-block mx-2"
                :class="{
                    'order-3': logoPosition === 'left',
                    'order-2': logoPosition === 'centred',
                }"
            >
                <DDropdown>
                    <template #trigger="dropdownProps">
                        <DButton
                            theme="transparent"
                            right-icon="caret-down"
                            icon-set="ph"
                            size="lg"
                            :active="dropdownProps.active"
                        >
                            {{ quicklinks.mainNavQuicklinksItem_Heading }}
                        </DButton>
                    </template>

                    <NuxtLink
                        v-for="(quicklink, index) in quicklinks.mainNavQuicklinksItem_Links"
                        :key="index"
                        :to="quicklink.url"
                        :target="quicklink.target"
                        no-prefetch
                    >
                        <DDropdownItem
                            :left-icon="quicklink?.icon?.name"
                            :icon-set="quicklink?.icon?.set"
                            :icon-type="quicklink?.icon?.type"
                        >
                            {{quicklink.text}}
                        </DDropdownItem>
                    </NuxtLink>
                </DDropdown>

            </div>

            <!-- buttons -->
            <div
                v-if="buttons.length > 0"
                class="hidden lg:flex items-center space-x-2 mx-2"
                :class="{
                    'order-4': logoPosition === 'left',
                    'order-5': logoPosition === 'centred',
                }"
            >
                <NuxtLink
                    v-for="(button, buttonIndex) in buttons"
                    :key="buttonIndex"
                    :to="button.url"
                    :target="button.target"
                    no-prefetch
                >
                    <DButton :theme="buttonTheme(button.buttonStyle)" size="lg">
                        {{ button.text }}
                    </DButton>
                </NuxtLink>
            </div>

            <!-- menu -->
            <div
                class="flex-shrink-0"
                :class="{
                    'order-5': logoPosition === 'left',
                    'order-1': logoPosition === 'centred',
                }"
            >
                <DButton
                    id="menubutton"
                    right-icon="list"
                    icon-set="ph"
                    theme="primary-subtle"
                    size="lg"
                    class="hidden lg:flex"
                    type="button"
                    aria-haspopup="true"
                    aria-controls="hamburger-menu"
                    aria-expanded="false"
                    @click="showMenu = true"
                >
                    Menu
                </DButton>

                <DButton
                    icon="list"
                    icon-set="ph"
                    theme="primary-subtle"
                    class="lg:hidden"
                    size="lg"
                    type="button"
                    aria-haspopup="true"
                    aria-controls="hamburger-menu"
                    aria-expanded="false"
                    @click="showMenu = true"
                />
            </div>
        </div>

        <MenuHamburger
            v-if="showMenu"
            :settings="settings"
            :entries="entries"
            :position="logoPosition === 'centred' ? 'left' : 'right'"
            :show-search="showSearch"
            @close="showMenu = false"
        />
    </div>
</template>

<script setup>
    import { DDropdown, DDropdownItem, DButton } from '@digistorm/spark'

    const props = defineProps({
        settings: Object,
        entries: Array,
        hasBanner: {
            type: Boolean,
            default: true,
        },
        homeUri: String,
        showSearch: Boolean,
    })

    const { bannerHasBorder } = storeToRefs(useSiteStore())

    const { buttonTheme } = useButtonTheme()

    const hasScrolled = ref(false)

    const showMenu = ref(false)

    const navOverlayBanner = computed(() => {
        return !props.settings.mainNav_ShowBackground && props.hasBanner
    })

    const logoPosition = computed(() => {
        return props.settings.mainNav_LogoPosition
    })

    const quicklinks = computed(() => {
        return first(props.settings.mainNav_Quicklinks)
    })

    const buttons = computed(() => {
        return props.settings.mainNav_Buttons ? props.settings.mainNav_Buttons.filter(button => !button.isEmpty) : []
    })

    const handleScroll = () => {
        // offset nav animation by 50px
        hasScrolled.value = window.scrollY > 100
    }

    onMounted(() => {
        window.addEventListener('scroll', handleScroll)
    })

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', handleScroll)
    })

</script>

<style lang="scss" scoped>

</style>